import { createTheme } from '@mui/material';
import { toNumber } from 'lodash';
import { DASHBOARD_DARK } from './dashboardDarkPalette';
import overrides from './overrides';
import palette from './palette';
import { ITheme, IThemeOptions } from './types';
import typography from './typography';

export { DASHBOARD_DARK } from './dashboardDarkPalette';

export const NAVIGATION_BAR_HEIGHT = '48';
export const PROGRESS_BAR_HEIGHT = '4';
export const TOOLBAR_HEIGHT = '100';

export const SIDEBAR_CLOSED_WIDTH = 60;

export const HEADER_HEIGHT =
  toNumber(TOOLBAR_HEIGHT) + toNumber(NAVIGATION_BAR_HEIGHT) + toNumber(PROGRESS_BAR_HEIGHT);

export const FIXED_HEADER_STYLES = Object.freeze({
  left: `${SIDEBAR_CLOSED_WIDTH}px`,
  top: `calc(${HEADER_HEIGHT}px - ${PROGRESS_BAR_HEIGHT}px)`,
  width: `calc(100% - ${SIDEBAR_CLOSED_WIDTH}px)`,
});

export const themeOptions: IThemeOptions = Object.freeze({
  cellPadding: 5,
  overrides,
  palette,
  typography,
  zIndex: {
    appBar: 1200,
    drawer: 1300,
  },
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: palette.secondary[500],
          },
        },
      },
    },
  },
});

const theme = createTheme(themeOptions) as unknown as ITheme;

/**
 * Dark theme — scoped to workboard via nested ThemeProvider in DashboardLayout.
 *
 * Built from scratch (NOT spreading themeOptions) so it doesn't inherit:
 *   - typography with hardcoded blueGrey light colors
 *   - overrides with hardcoded light palette references
 *
 * MUI palette.mode:'dark' handles backgrounds, text, and dividers automatically.
 * To enable dark mode in another layout, wrap it with:
 *   <ThemeProvider theme={resolvedMode === 'dark' ? darkTheme : lightTheme}>
 */
export const darkTheme = createTheme({
  // Structural options only — no color-dependent overrides
  cellPadding: 5,
  zIndex: {
    appBar: 1200,
    drawer: 1300,
  },
  // Curated dark palette — layered neutrals (see `dashboardDarkPalette.ts`)
  palette: {
    mode: 'dark',
    background: {
      default: DASHBOARD_DARK.canvas,
      paper: DASHBOARD_DARK.surface,
    },
    text: {
      primary: DASHBOARD_DARK.textPrimary,
      secondary: DASHBOARD_DARK.textSecondary,
      disabled: DASHBOARD_DARK.textDisabled,
    },
    divider: DASHBOARD_DARK.hairline,
    /**
     * MUI uses `action.active` as the default IconButton / control color (see IconButton.js).
     * Must be a light rgba on dark — never reuse accentTint here or icons disappear.
     */
    action: {
      active: 'rgba(255, 255, 255, 0.78)',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
      selectedOpacity: 0.12,
      disabled: 'rgba(255, 255, 255, 0.34)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
      disabledOpacity: 0.38,
      focus: 'rgba(255, 255, 255, 0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
      hoverOpacity: 0.1,
    },
    // Dark mode uses a softer accent (#4F8CFF) instead of the light-mode primary
    // (#037de8). Lower chroma, ~55% lightness — reads clearly on dark surfaces
    // without the "electric" harshness of fully-saturated blues.
    primary: {
      main: DASHBOARD_DARK.accent,
      light: DASHBOARD_DARK.accentHover,
      dark: DASHBOARD_DARK.accentActive,
      contrastText: '#ffffff',
    },
    secondary: {
      main: DASHBOARD_DARK.accent,
      light: DASHBOARD_DARK.accentHover,
      dark: DASHBOARD_DARK.accentActive,
      contrastText: '#ffffff',
    },
    error: {
      main: palette.error.main,
      light: palette.error.light,
      dark: palette.error.dark,
      contrastText: palette.error.contrastText,
    },
    success: palette.success,
    warning: {
      main: palette.warning.main,
      light: palette.warning.light,
      dark: palette.warning.dark,
      contrastText: palette.warning.contrastText,
    },
    info: palette.info,
  },
  // Remove MUI Paper elevation overlay (white gradient) in dark mode — we use solid backgrounds
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
  // Typography with font families/sizes only — colors inherit from dark palette automatically
  typography: {
    mainFont: '"Open Sans", sans-serif',
    secondaryFont: '"Open Sans", sans-serif',
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 18,
    htmlFontSize: 18,
    h1: { fontWeight: 400, fontSize: '28px', letterSpacing: '-0.24px', lineHeight: '40px' },
    h2: { fontWeight: 700, fontSize: '22px', letterSpacing: '-0.24px', lineHeight: '32px' },
    h3: { fontWeight: 700, fontSize: '18px', letterSpacing: '-0.06px', lineHeight: '28px' },
    h4: { fontWeight: 700, fontSize: '16px', letterSpacing: '-0.06px', lineHeight: '24px' },
    h5: { fontWeight: 700, fontSize: '14px', letterSpacing: '-0.05px', lineHeight: '20px' },
    h6: { fontWeight: 700, fontSize: '12px', letterSpacing: '-0.05px', lineHeight: '20px' },
    subtitle1: { fontSize: '16px', letterSpacing: '-0.05px', lineHeight: '25px' },
    subtitle2: { fontWeight: 400, fontSize: '14px', letterSpacing: '-0.05px', lineHeight: '21px' },
    body1: { fontWeight: 400, fontSize: '16px', letterSpacing: '-0.05px', lineHeight: '21px' },
    body2: { fontWeight: 400, fontSize: '14px', letterSpacing: '-0.04px', lineHeight: '18px' },
    caption: { fontSize: '11px', letterSpacing: '0.33px', lineHeight: '13px' },
    overline: { fontWeight: 500, fontSize: '11px', letterSpacing: '0.33px', lineHeight: '13px', textTransform: 'uppercase' as const },
    button: { fontWeight: 700, textTransform: 'uppercase' as const },
  },
} as unknown as IThemeOptions) as unknown as ITheme;

export default theme;
